import { useState } from 'react';
import { Plus, MinusCircle, History, Pencil, CheckCircle2, Bell, ClipboardCheck } from 'lucide-react';
import type { InsumoVista } from '@shared/types';
import { esIndivisible } from '@shared/fraccionInsumo';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 15 — Tarjeta de insumo para grid de /inventario.
 *
 * Diseño UX:
 *  - 1 insumo = 1 unidad mental focalizada (vs tabla de 5+ columnas).
 *  - Semáforo prominente en el header (color + texto), no diluido entre celdas.
 *  - "Ya cargado hoy" con check verde, prominente, para evitar el bug histórico
 *    de duplicación de carga (Sprint 12.1e — bug del 48.500).
 *  - Botón "+ Agregar más stock" cumple Fitts holgado (≥44 px alto).
 *  - Acciones secundarias (merma, bitácora, editar, eliminar) en una fila
 *    inferior con íconos pero etiquetas accesibles.
 */

export type Semaforo = 'agotado' | 'critico' | 'bajo' | 'ok';

export function calcularSemaforo(stock: number, minimo: number): Semaforo {
  if (stock <= 0) return 'agotado';
  if (minimo > 0 && stock <= minimo) return 'critico';
  if (minimo > 0 && stock <= minimo * 2) return 'bajo';
  return 'ok';
}

const SEMAFORO_STYLE: Record<Semaforo, { borde: string; fondo: string; chip: string; label: string }> = {
  agotado:  { borde: 'border-slate-400', fondo: 'bg-slate-100',  chip: 'bg-slate-600 text-white',    label: '⚫ AGOTADO' },
  critico:  { borde: 'border-red-400',   fondo: 'bg-red-50',     chip: 'bg-red-600 text-white',      label: '🔴 CRÍTICO' },
  bajo:     { borde: 'border-amber-400', fondo: 'bg-amber-50',   chip: 'bg-amber-500 text-white',    label: '🟡 BAJO' },
  ok:       { borde: 'border-emerald-200', fondo: 'bg-white',    chip: 'bg-emerald-100 text-emerald-800', label: '🟢 OK' },
};

interface Props {
  insumo: InsumoVista;
  onCargar: () => void;
  /** Sprint 133 — Poner la cantidad que de verdad hay (ajuste con motivo). */
  onCorregir: () => void;
  onMerma: () => void;
  onBitacora: () => void;
  /** Sprint 110 — Lleva a Productos, que es donde se administra lo que se vende. */
  onVerEnMenu: () => void;
  /** Sprint 124 — Se cambió el umbral de aviso: el padre recarga para repintar el semáforo. */
  onCambio?: () => void;
}

export function InsumoCard({ insumo, onCargar, onCorregir, onMerma, onBitacora, onVerEnMenu, onCambio }: Props) {
  const sem = calcularSemaforo(insumo.stockActual, insumo.stockMinimo);
  const style = SEMAFORO_STYLE[sem];
  const merma = insumo.mermaCoccionPct ?? 0;
  const fraccion = insumo.fraccionMinima ?? null;
  /**
   * Sprint 63 — Ya no se calcula un "para vender" aparte: el stock YA es lo disponible
   * (la merma se aplicó al cargar). Antes esta tarjeta dividía otra vez y mostraba un
   * número menor que lo que el POS podía servir — dos cifras distintas para la misma
   * cosa, en pantallas contiguas.
   */
  /** Sin decimales en lo que se cuenta por piezas: "30 piezas", no "30.00". */
  const fmt = (n: number) => (esIndivisible(fraccion) ? String(Math.round(n)) : n.toFixed(2));
  const cargadoHoy = insumo.cargadoHoy ?? 0;

  const [editandoAlerta, setEditandoAlerta] = useState(false);
  const [borrador, setBorrador] = useState('');
  const [guardando, setGuardando] = useState(false);

  /**
   * Guarda sólo el umbral de aviso.
   *
   * Vacío se guarda como 0, que es "no me avises": es una respuesta legítima —hay cosas que no
   * hace falta vigilar— y no lo mismo que dejarlo sin contestar. La diferencia se ve en la
   * pantalla: sin umbral el número se pinta en ámbar, porque el semáforo no puede encenderse.
   */
  const guardarAlerta = async () => {
    const valor = borrador.trim() === '' ? 0 : Number(borrador);
    if (Number.isNaN(valor) || valor < 0) {
      await window.alertar({
        titulo: 'Ese número no se entiende',
        mensaje: 'Escribe con cuánto quieres que te avise, por ejemplo 3.',
        variant: 'warn',
      });
      return;
    }
    setGuardando(true);
    const r = await window.api.insumos.actualizar(insumo.id, { stockMinimo: valor });
    setGuardando(false);
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo guardar la alerta',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    setEditandoAlerta(false);
    onCambio?.();
  };

  return (
    <div className={`rounded-xl border-2 ${style.borde} ${style.fondo} p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow`}>
      {/* Chip semáforo */}
      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide ${style.chip}`}>
          {style.label}
        </span>
        {merma > 0 && (
          <span className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
            merma {merma.toFixed(0)}%
          </span>
        )}
      </div>

      {/* Nombre prominente */}
      <h3 className="text-lg font-bold leading-tight text-slate-800">{insumo.nombre}</h3>

      {/* Métricas */}
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-600">Disponible para vender:</span>
          <span className="font-mono font-semibold">
            {fmt(insumo.stockActual)} {insumo.unidad}
          </span>
        </div>
        {/*
          Sprint 63 — Ya no hay dos números: el stock ES lo vendible. Lo que aporta
          aquí es recordar que la merma se descuenta al cargar, no al vender.
        */}
        {merma > 0 && (
          <div className="text-[11px] leading-snug text-slate-500">
            Se descuenta {merma.toFixed(0)}% al cargar, por la cocción. Lo de arriba ya es lo que
            puedes servir.
          </div>
        )}
        {/*
          Sprint 125 — Era un botón que no parecía un botón.

          El Sprint 124 abrió la edición del umbral… en texto gris de 12 px con subrayado punteado,
          al final de una lista de datos. El dueño, que es informático y sabía que existía, no lo
          encontró: *"pinche letra minúscula descolorida, hasta parece que juega a las escondidas"*.
          Y la regla del propio proyecto pide 44 px para lo que se toca — aquello medía 16.

          Dos errores en uno: **darle a una acción el aspecto de un dato**, y ponerla dentro del
          bloque de métricas, que es justo donde el ojo aprende a no tocar nada.

          Ahora es una franja de ancho completo, con campana, con su color y con la frase escrita
          como se dice en el piso — *"avísame cuando queden 3"*, no *"alerta si baja a: 3"*. Y
          cuando falta, se pinta en ámbar y lo pide: es lo que hace que el semáforo pueda encenderse.
        */}
      </div>

      <button
        onClick={() => {
          if (editandoAlerta) return;
          setBorrador(insumo.stockMinimo ? String(insumo.stockMinimo) : '');
          setEditandoAlerta(true);
        }}
        className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg border-2 px-3 py-2 text-left text-sm transition ${
          insumo.stockMinimo
            ? 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
            : 'border-amber-300 bg-amber-50 font-semibold text-amber-800 hover:bg-amber-100'
        }`}
      >
        <Bell className={`h-4 w-4 shrink-0 ${insumo.stockMinimo ? 'text-slate-400' : 'text-amber-600'}`} />
        {editandoAlerta ? (
          <span className="flex flex-1 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="shrink-0">Avísame cuando queden</span>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={borrador}
              onChange={(e) => setBorrador(e.target.value.replace(/[^\d.]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void guardarAlerta();
                if (e.key === 'Escape') setEditandoAlerta(false);
              }}
              aria-label={`Avísame cuando queden menos de tantos ${insumo.unidad} de ${insumo.nombre}`}
              className="input h-9 w-16 text-center font-mono"
              disabled={guardando}
            />
            <span
              role="button"
              tabIndex={0}
              onClick={() => void guardarAlerta()}
              onKeyDown={(e) => e.key === 'Enter' && void guardarAlerta()}
              className="ml-auto shrink-0 rounded-md bg-primary px-3 py-1.5 font-semibold text-primary-fg"
            >
              {guardando ? '…' : 'Listo'}
            </span>
          </span>
        ) : insumo.stockMinimo ? (
          <>
            <span>
              Avísame cuando queden <strong className="font-mono">{fmt(insumo.stockMinimo)}</strong>{' '}
              {insumo.unidad}
            </span>
            <span className="ml-auto shrink-0 text-xs text-slate-400">cambiar</span>
          </>
        ) : (
          <span>Dime cuándo avisarte</span>
        )}
      </button>

      {/* Ya cargaste hoy — prominente */}
      {cargadoHoy > 0 ? (
        <div className="rounded-lg bg-emerald-100 border border-emerald-200 px-3 py-2 flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>
            <strong>Ya cargaste hoy:</strong> +{cargadoHoy.toFixed(2)} {insumo.unidad}
          </span>
        </div>
      ) : (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500 text-center italic">
          Aún no cargas nada hoy.
        </div>
      )}

      {/* Acción principal grande */}
      <button
        onClick={onCargar}
        className="btn-primary w-full justify-center"
        style={{ minHeight: '44px' }}
      >
        <Plus className="h-5 w-5" />
        Agregar más stock
      </button>

      {/*
        Sprint 133 — "Me equivoqué al ingresar las cantidades, ¿cómo puedo corregir?".

        Pregunta de la dueña su primer día en producción. `registrarAjuste` existía en el servidor
        desde hacía sprints —con motivo obligatorio y su movimiento con rastro— y **no tenía
        ninguna puerta**: la única salida era registrar una merma falsa, o sea ensuciar el dato que
        el negocio usa para costear con un dedazo de captura.

        Va debajo de "Agregar más stock" y no en la fila de acciones pequeñas, porque son las dos
        caras de lo mismo —lo que entra y lo que hay— y porque en esa fila ya no caben tres
        etiquetas (lección del Sprint 118). Con su borde y sus 44 px: es un control, y se maqueta
        como un control (lección del Sprint 125).
      */}
      <button
        onClick={onCorregir}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition hover:border-slate-300"
        title="Poner la cantidad que de verdad hay"
      >
        <ClipboardCheck className="h-4 w-4 shrink-0 text-slate-500" />
        Corregir existencia
      </button>

      {/* Acciones secundarias */}
      <div className="flex justify-between items-center border-t border-slate-200 pt-2 -mx-2 px-2">
        <button
          onClick={onMerma}
          title="Registrar merma o daño"
          className="btn-ghost text-red-600 px-2"
        >
          <MinusCircle className="h-4 w-4" />
          <span className="text-xs ml-1">Merma</span>
        </button>
        <button
          onClick={onBitacora}
          title="Ver bitácora"
          className="btn-ghost text-slate-600 px-2"
        >
          <History className="h-4 w-4" />
          <span className="text-xs ml-1">Historial</span>
        </button>
        {/*
          Sprint 110 — Aquí ya no se edita el catálogo ni se borra nada.

          Había un lápiz (cambiaba nombre, unidad y merma) y un bote de basura. La operadora usó el
          bote para "limpiar" y el resultado fue el peor estado en el que ha estado la base: 41
          ingredientes apagados —invisibles, pero bloqueando el alta por nombre— y **27 productos
          siguiendo a la venta** descontando de un almacén que ya no existía. No hizo nada indebido:
          el botón estaba ahí, con su ícono de basura, en la única pantalla donde veía todo junto.

          Una pantalla que no da de alta tampoco puede dar de baja. Lo que se vende se administra en
          **Productos**: ahí está "Editar tamaños y precios", "Hoy no lo tengo" y "Ya no lo vendo",
          y esa baja se lleva las dos mitades de la misma cosa.

          Aquí queda lo que de verdad es de existencias: cargar, mermar y consultar.
        */}
        {/*
          Sprint 118 — "Editar en Productos" se partía en dos líneas y se salía de la tarjeta.

          Tres acciones con su ícono y su palabra no caben en el ancho de una tarjeta de la rejilla,
          y la tercera —la más larga— se desbordaba encimándose con el borde. Ahora dice **"Editar"**
          y el destino vive en el `title`: la fila cabe, y quien dude lo que hace ya tiene el ícono
          del lápiz al lado. Se acorta la etiqueta, no la función.
        */}
        <button
          onClick={onVerEnMenu}
          title="Editar en Productos: cambiar su precio, nombre o tamaños"
          className="btn-ghost whitespace-nowrap px-2 text-slate-600"
        >
          <Pencil className="h-4 w-4" />
          <span className="ml-1 text-xs">Editar</span>
        </button>
      </div>
    </div>
  );
}
